import {
  AlertTriangle,
  CreditCard,
  MessageCircle,
  ArrowLeft,
  ArrowUpRight,
} from "lucide-react";

const SUPPORT_WHATSAPP_LINK = `https://wa.me/15557482413?text=${encodeURIComponent(
  "Hello, my subscription has expired. I'd like to renew my plan."
)}`;

const SubscriptionExpiredPage = () => {
  const steps = [
    {
      icon: CreditCard,
      description: "Your subscription has reached its expiration date.",
      status: "expired",
    },
    {
      icon: AlertTriangle,
      description:
        "Your grace period has ended, so access to your account is temporarily restricted.",
      status: "warning",
    },
    {
      icon: MessageCircle,
      label: "Contact support to renew your subscription.",
      description: null,
      status: "pending",
    },
  ];

  const statusStyles = {
    expired: {
      icon: "text-red-600",
      bg: "bg-red-50",
      row: "bg-gray-50 border-gray-100",
    },
    warning: {
      icon: "text-orange-500",
      bg: "bg-orange-50",
      row: "bg-gray-50 border-gray-100",
    },
    pending: {
      icon: "text-gray-400",
      bg: "bg-gray-100",
      row: "bg-gray-50 border-gray-100",
    },
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-[420px]">
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="h-[3px] bg-gradient-to-r from-red-500 to-orange-400" />

          <div className="px-7 pt-8 pb-7">
            {/* Icon */}
            <div className="flex flex-col items-center mb-6">
              <div className="relative w-16 h-16 mb-3 animate-[float_3s_ease-in-out_infinite]">
                <div className="w-full h-full rounded-2xl bg-red-50 flex items-center justify-center">
                  <AlertTriangle size={28} className="text-red-500" />
                </div>
              </div>

              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 border border-red-100 text-red-700 text-[12px] font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
                Subscription expired
              </span>
            </div>

            <h1 className="text-[20px] font-bold text-gray-900 text-center mb-2">
              Your subscription has expired
            </h1>

            <p className="text-sm text-gray-500 text-center leading-relaxed mb-6">
              Your subscription and grace period have ended. Renew your plan to
              regain access to your account.
            </p>

            {/* Steps */}
            <div className="flex flex-col gap-2 mb-6">
              {steps.map(({ icon: Icon, label, description, status }) => {
                const s = statusStyles[status];

                return (
                  <div
                    key={label || description}
                    className={`flex items-start gap-3 p-3 rounded-xl border ${s.row}`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center ${s.bg}`}
                    >
                      <Icon size={15} className={s.icon} />
                    </div>

                    <div>
                      {label && (
                        <p className="text-[13px] text-gray-500">{label}</p>
                      )}

                      {description && (
                        <p className="text-[13px] font-semibold text-gray-800 leading-snug">
                          {description}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* WhatsApp */}
            <a
              href={SUPPORT_WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="w-full flex items-center justify-center gap-2.5 py-3.5 px-5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition-all duration-150 mb-2.5"
            >
              <MessageCircle size={18} />
              Renew via WhatsApp
              <ArrowUpRight size={14} className="ml-auto" />
            </a>

            {/* Back */}
            <a
              href="/login"
              className="w-full flex items-center justify-center gap-2 py-3 px-5 bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-900 text-sm font-medium rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-150"
            >
              <ArrowLeft size={15} />
              Back to login
            </a>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
      `}</style>
    </div>
  );
};

export default SubscriptionExpiredPage;